import { useEffect, useState } from "react";
import { createCheckout, getPaymentMethod, subscribeWithSavedCard } from "./me-api";
import { useMe } from "./useMe";
import { useAuth } from "../auth";
import { useToast } from "../ui/Toast";

interface Props {
  open: boolean;
  feature: string;
  onClose: () => void;
  /** Pre-login only: where to send the user to create an account. Stripe
   *  Checkout requires a signed-in user, so an unauthenticated visitor must
   *  sign up first. Omit on post-login (authenticated) screens. */
  onSignUp?: () => void;
}

interface Entry {
  heading: string;
  body: string;
  /** Secondary "what the plan unlocks" paragraph. Omit for coming-soon contexts. */
  detail?: string;
  /** Override the default "Email us to upgrade" CTA label. */
  ctaLabel?: string;
}

const DEFAULT_DETAIL =
  "The Pro plan unlocks unlimited canvases, the coach, scheduled revisits, on-demand reports, document upload, and Phase 10 canvas review.";

const ENTRIES: Record<string, Entry> = {
  coach: {
    heading: "Unlock the coach",
    body: "Continue the conversation with the coach about any layer, gap, or recent incident.",
    detail: DEFAULT_DETAIL,
  },
  reminder: {
    heading: "Unlock scheduled revisits",
    body: "Schedule a revisit email + ICS calendar invite to keep your canvas honest.",
    detail: DEFAULT_DETAIL,
  },
  "report-variant": {
    heading: "Unlock on-demand reports",
    body: "Generate gap summaries, scorecards, consistency reports, and executive one-pagers on demand.",
    detail: DEFAULT_DETAIL,
  },
  "doc-upload": {
    heading: "Unlock document upload",
    body: "Upload a strategy doc or PRD and let the coach pre-fill your answers for review.",
    detail: DEFAULT_DETAIL,
  },
  "phase-10-review": {
    heading: "Unlock Phase 10 review",
    body: "Re-walk your canvas in review mode and snapshot a new version with delta scoring.",
    detail: DEFAULT_DETAIL,
  },
  "individual-plan": {
    heading: "Go Pro — $149/mo",
    body: "Complete privacy (no data contribution), unlimited canvases, the coach, quarterly reviews, document upload, and on-demand reports.",
  },
};

const DEFAULT_ENTRY: Entry = {
  heading: "Upgrade to Pro",
  body: "This feature is part of the Pro plan.",
  detail: DEFAULT_DETAIL,
};

export function UpgradeModal({ open, feature, onClose, onSignUp }: Props) {
  const { user } = useAuth();
  const { refresh: refreshMe } = useMe();
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedCard, setSavedCard] = useState<{ brand: string; last4: string } | null>(null);

  // On open (authenticated), look up the card on file so we can offer a
  // one-click charge instead of re-collecting card details.
  useEffect(() => {
    if (!open || !user) return;
    let cancelled = false;
    getPaymentMethod()
      .then((c) => {
        if (!cancelled) setSavedCard(c);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [open, user]);

  if (!open) return null;
  const entry = ENTRIES[feature] ?? DEFAULT_ENTRY;

  const goToCheckout = async () => {
    // Redirect to the hosted Stripe Checkout page (collects a new card).
    window.location.href = await createCheckout("monthly");
  };

  // Primary CTA: signed out → sign up; card on file → charge it; else Checkout.
  const ctaLabel = !user
    ? "Start free →"
    : savedCard
      ? `Upgrade — pay with ${brandLabel(savedCard.brand)} ···· ${savedCard.last4}`
      : (entry.ctaLabel ?? "Upgrade — $149/mo");

  const handleUpgrade = async () => {
    if (!user) {
      (onSignUp ?? onClose)();
      return;
    }
    setBusy(true);
    setError(null);
    try {
      if (savedCard) {
        const r = await subscribeWithSavedCard("monthly");
        if (r.ok) {
          await refreshMe();
          toast.success("Welcome to Pro!");
          onClose();
          return;
        }
        // Card needs authentication / declined → fall back to Checkout.
      }
      await goToCheckout();
    } catch (e) {
      setError((e as Error).message);
      setBusy(false);
    }
  };

  const handleUseDifferentCard = async () => {
    setBusy(true);
    setError(null);
    try {
      await goToCheckout();
    } catch (e) {
      setError((e as Error).message);
      setBusy(false);
    }
  };

  return (
    <div className="upgrade-modal__backdrop" onClick={onClose}>
      <div
        className="upgrade-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="upgrade-modal-heading"
        onClick={(e) => e.stopPropagation()}
      >
        <header>
          <span className="upgrade-modal__eyebrow">Pro plan — $149/mo</span>
          <h2 id="upgrade-modal-heading">{entry.heading}</h2>
        </header>
        <p>{entry.body}</p>
        {entry.detail && <p className="upgrade-modal__detail">{entry.detail}</p>}
        {!user && (
          <p className="upgrade-modal__detail">
            Create your free account first — then upgrade to Pro in one click from
            the <strong>Upgrade to Pro</strong> button on your dashboard.
          </p>
        )}
        {user && savedCard && (
          <p className="upgrade-modal__detail">
            Card on file: <strong>{brandLabel(savedCard.brand)} ···· {savedCard.last4}</strong>.
            Charge it now, or use a different card.
          </p>
        )}
        {error && <div className="banner banner--error">{error}</div>}
        <div className="upgrade-modal__actions">
          <button
            className="btn btn--primary"
            onClick={() => void handleUpgrade()}
            disabled={busy}
          >
            {busy ? "Working…" : ctaLabel}
          </button>
          {user && savedCard && (
            <button
              className="btn btn--text"
              onClick={() => void handleUseDifferentCard()}
              disabled={busy}
            >
              Use a different card
            </button>
          )}
          <button className="btn btn--text" onClick={onClose} disabled={busy}>
            Maybe later
          </button>
        </div>
      </div>
    </div>
  );
}

function brandLabel(brand: string): string {
  const map: Record<string, string> = {
    visa: "Visa",
    mastercard: "Mastercard",
    amex: "Amex",
    discover: "Discover",
    diners: "Diners",
    jcb: "JCB",
    unionpay: "UnionPay",
  };
  return map[brand] ?? (brand ? brand[0].toUpperCase() + brand.slice(1) : "Card");
}
